import { useState, useEffect } from 'react';
import { PageHelp } from '@/components/ui/PageHelp';
import {
  Home, Clock, Users, Calendar, Gift, Lock, Save, Plus, Trash2,
  Sparkles, AlertCircle, Check, X, Stethoscope, ShieldAlert, Monitor, Eye,
} from 'lucide-react';
import AppointmentCatalogSection from './AppointmentCatalogSection';
import {
  usePharmacySettings, useUpdatePharmacy, useUpdateTeamPin,
  useUpdateOpeningHours, useUpdateStaffingRequirements,
  useHolidays, useSeedFrenchHolidays, useCreateHoliday, useUpdateHoliday, useDeleteHoliday,
  usePrimes, useCreatePrime, useUpdatePrime, useDeletePrime,
  useSites, useCreateSite,
  useResetPreview, useResetPharmacy,
  useViewers, useAddViewer, useRemoveViewer,
} from '@/api/resources';
import { Button } from '@/components/ui/Button';
import { auth, ApiError } from '@/api/client';
import { Chip } from '@/components/ui/Chip';
import type { VariableStaffMember } from '@/types';
import VariableFillModal from './VariableFillModal';
import { useOpenCounter } from '@/api/auth';
import { counter } from '@/lib/counterMode';
import DutySection from './DutySection';

type SectionId = 'pharmacy' | 'opening' | 'staffing' | 'catalog' | 'holidays' | 'primes' | 'counter' | 'viewers' | 'danger';

const DAYS_FR = ['Dimanche', 'Lundi', 'Mardi', 'Mercredi', 'Jeudi', 'Vendredi', 'Samedi'];

const MISSIONS = [
  { v: 'COMPTOIR', l: 'Comptoir', d: 'Accueil et vente au public' },
  { v: 'BACKOFFICE_PHARMA', l: 'Back office pharmacie', d: 'Délivrance, stupéfiants, gestion arrière' },
  { v: 'BACKOFFICE_TECHNIQUE', l: 'Back office technique', d: 'Stocks, commandes, réception' },
  { v: 'VACCINATION', l: 'Vaccination', d: 'Campagnes vaccinales (saisonnier)' },
  { v: 'PDA', l: 'PDA', d: 'Préparation de doses à administrer' },
  { v: 'PREPARATOIRE', l: 'Préparatoire', d: 'Préparations magistrales' },
  { v: 'TROD', l: 'TROD', d: 'Tests rapides orientation diagnostique' },
  { v: 'ENTRETIEN', l: 'Entretiens', d: 'Entretiens pharmaceutiques' },
  { v: 'BILAN', l: 'Bilans', d: 'Bilans de médication' },
  { v: 'OUVERTURE', l: 'Ouverture', d: 'Personne qui ouvre l\'officine' },
  { v: 'FERMETURE', l: 'Fermeture', d: 'Personne qui ferme l\'officine' },
];

// Missions de « présence » (ouverture/fermeture) : effectif détaillé PAR POSTE (R11).
const PRESENCE_MISSIONS = new Set(['OUVERTURE', 'FERMETURE']);
// Postes proposés pour le détail de l'effectif requis à l'ouverture/fermeture.
const STAFFING_POSITIONS: { v: string; l: string }[] = [
  { v: 'PHARMACIEN', l: 'Pharmacien' },
  { v: 'PREPARATEUR', l: 'Préparateur' },
  { v: 'ETUDIANT', l: 'Étudiant' },
  { v: 'MAGASINIER', l: 'Magasinier' },
];

export default function SettingsPage() {
  const [section, setSection] = useState<SectionId>('pharmacy');

  const sections: { id: SectionId; label: string; icon: typeof Home; caption: string }[] = [
    { id: 'pharmacy', label: 'Pharmacie', icon: Home, caption: 'Informations générales' },
    { id: 'opening', label: 'Horaires d\'ouverture', icon: Clock, caption: 'Par jour de la semaine' },
    { id: 'staffing', label: 'Effectifs requis', icon: Users, caption: 'Par mission et période' },
    { id: 'catalog', label: 'Prestations sur RDV', icon: Stethoscope, caption: 'Compétence requise par prestation' },
    { id: 'holidays', label: 'Jours fériés', icon: Calendar, caption: 'Fermetures et majorations' },
    { id: 'primes', label: 'Primes et règles', icon: Gift, caption: 'Rémunération variable' },
    { id: 'counter', label: 'Poste comptoir', icon: Monitor, caption: "Ouvrir cet ordinateur à l'équipe" },
    { id: 'viewers', label: 'Accès consultation', icon: Eye, caption: 'Voir le RH sans le modifier' },
    { id: 'danger', label: 'Zone de danger', icon: ShieldAlert, caption: 'Réinitialiser la pharmacie' },
  ];

  return (
    <div className="flex flex-col h-full">
      <div className="border-b border-slate-200 px-8 py-4">
        <h1 className="text-[22px] font-semibold tracking-tight">Paramètres de la pharmacie <PageHelp topic="settings" /></h1>
        <div className="text-[12px] text-slate-500 mt-0.5">
          Configurez votre officine — ces règles alimentent le moteur d'organisation et la fiche navette.
        </div>
      </div>

      <div className="flex-1 overflow-hidden flex">
        {/* Sidebar de sous-navigation */}
        <aside className="w-64 border-r border-slate-200 bg-slate-50/50 overflow-y-auto">
          <nav className="p-3 space-y-0.5">
            {sections.map((s) => {
              const Icon = s.icon;
              const active = section === s.id;
              return (
                <button
                  key={s.id}
                  onClick={() => setSection(s.id)}
                  className={`w-full flex items-start gap-2.5 px-3 py-2.5 rounded-lg text-left transition-colors ${
                    active ? 'bg-primary text-white' : 'text-slate-700 hover:bg-white'
                  }`}
                >
                  <Icon size={15} strokeWidth={1.75} className="mt-0.5 flex-shrink-0" />
                  <div className="min-w-0">
                    <div className="text-[12.5px] font-medium">{s.label}</div>
                    <div className={`text-[10.5px] mt-0.5 ${active ? 'text-white/70' : 'text-slate-500'}`}>{s.caption}</div>
                  </div>
                </button>
              );
            })}
          </nav>
        </aside>

        <main className="flex-1 overflow-y-auto">
          <div className="max-w-[840px] mx-auto p-8">
            {section === 'pharmacy' && <PharmacySection />}
            {section === 'opening' && <OpeningHoursSection />}
            {section === 'staffing' && <StaffingSection />}
            {section === 'catalog' && <AppointmentCatalogSection />}
            {section === 'holidays' && <HolidaysSection />}
            {section === 'primes' && <PrimesSection />}
            {section === 'counter' && <CounterPostSection />}
            {section === 'viewers' && <ViewerAccessSection />}
            {section === 'danger' && <DangerZoneSection />}
          </div>
        </main>
      </div>
    </div>
  );
}

// ============================================================================
// Helpers communs
// ============================================================================

function Section({ title, description, children, action }: { title: string; description?: string; children: React.ReactNode; action?: React.ReactNode }) {
  return (
    <div className="mb-8">
      <div className="mb-3 flex items-start justify-between gap-4">
        <div>
          <h2 className="text-[15px] font-semibold text-slate-900">{title}</h2>
          {description && <div className="text-[11.5px] text-slate-500 mt-0.5">{description}</div>}
        </div>
        {action}
      </div>
      <div className="bg-white border border-slate-200 rounded-xl p-5">
        {children}
      </div>
    </div>
  );
}

function SaveBanner({ isPending, error, success, successMessage }: { isPending: boolean; error?: Error | null; success?: boolean; successMessage?: string }) {
  if (isPending) return null;
  if (error) {
    return (
      <div className="bg-danger-bg border border-danger/30 rounded-lg p-3 flex items-start gap-2 text-[12px] text-danger mb-4">
        <AlertCircle size={13} className="mt-0.5 flex-shrink-0" />
        {error.message}
      </div>
    );
  }
  if (success) {
    return (
      <div className="bg-primary-bg border border-primary/30 rounded-lg p-3 flex items-start gap-2 text-[12px] text-primary mb-4">
        <Check size={13} className="mt-0.5 flex-shrink-0" />
        {successMessage ?? 'Modifications enregistrées'}
      </div>
    );
  }
  return null;
}

// ============================================================================
// 1. PHARMACY
// ============================================================================

function PharmacySection() {
  const { data: pharmacy, isLoading } = usePharmacySettings();
  const updateMut = useUpdatePharmacy();
  const pinMut = useUpdateTeamPin();

  const [form, setForm] = useState({ name: '', siret: '', shuttleEmail: '', address: '', city: '', postalCode: '', maxReinforcementsPerHalfDay: 4 });
  const [newPin, setNewPin] = useState('');
  const [pinSuccess, setPinSuccess] = useState(false);

  useEffect(() => {
    if (pharmacy) {
      setForm({
        name: pharmacy.name ?? '',
        siret: pharmacy.siret ?? '',
        shuttleEmail: pharmacy.shuttleEmail ?? '',
        address: pharmacy.address ?? '',
        city: pharmacy.city ?? '',
        postalCode: pharmacy.postalCode ?? '',
        maxReinforcementsPerHalfDay: pharmacy.maxReinforcementsPerHalfDay ?? 4,
      });
    }
  }, [pharmacy]);

  const isDirty = pharmacy && (
    form.name !== (pharmacy.name ?? '') ||
    form.siret !== (pharmacy.siret ?? '') ||
    form.shuttleEmail !== (pharmacy.shuttleEmail ?? '') ||
    form.address !== (pharmacy.address ?? '') ||
    form.city !== (pharmacy.city ?? '') ||
    form.postalCode !== (pharmacy.postalCode ?? '') ||
    form.maxReinforcementsPerHalfDay !== (pharmacy.maxReinforcementsPerHalfDay ?? 4)
  );

  async function handleSave() {
    await updateMut.mutateAsync({
      name: form.name,
      siret: form.siret || null,
      shuttleEmail: form.shuttleEmail,
      address: form.address || null,
      city: form.city || null,
      postalCode: form.postalCode || null,
      maxReinforcementsPerHalfDay: form.maxReinforcementsPerHalfDay,
    });
  }

  async function handlePinChange() {
    if (!/^\d{4}$/.test(newPin)) return;
    await pinMut.mutateAsync(newPin);
    setPinSuccess(true);
    setNewPin('');
    setTimeout(() => setPinSuccess(false), 3000);
  }

  if (isLoading) return <div className="text-sm text-slate-500">Chargement…</div>;

  return (
    <>
      <SaveBanner isPending={updateMut.isPending} error={updateMut.error as Error | null} success={updateMut.isSuccess} />

      <Section title="Informations générales" description="Visible dans les fiches navette et les communications.">
        <div className="grid grid-cols-2 gap-4 mb-4">
          <div>
            <label className="label">Nom de l'officine</label>
            <input value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} className="input" />
          </div>
          <div>
            <label className="label">SIRET <span className="text-slate-400 font-normal">(facultatif)</span></label>
            <input value={form.siret} onChange={(e) => setForm({ ...form, siret: e.target.value })} placeholder="14 chiffres" className="input" />
          </div>
        </div>
        <label className="label">Email destinataire de la fiche navette</label>
        <input type="email" value={form.shuttleEmail} onChange={(e) => setForm({ ...form, shuttleEmail: e.target.value })} className="input" />
      </Section>

      <UsageStartSection />

      <Section title="Adresse">
        <label className="label">Adresse</label>
        <input value={form.address} onChange={(e) => setForm({ ...form, address: e.target.value })} placeholder="12 avenue de la République" className="input mb-3" />
        <div className="grid grid-cols-[120px_1fr] gap-4">
          <div>
            <label className="label">Code postal</label>
            <input value={form.postalCode} onChange={(e) => setForm({ ...form, postalCode: e.target.value })} placeholder="75011" className="input" />
          </div>
          <div>
            <label className="label">Ville</label>
            <input value={form.city} onChange={(e) => setForm({ ...form, city: e.target.value })} placeholder="Paris" className="input" />
          </div>
        </div>
      </Section>

      <Section
        title="Horaires variables"
        description="Comportement du comblement automatique des creux de couverture."
      >
        <div className="flex items-end gap-3">
          <div>
            <label className="label">Renforts maximum par demi-journée</label>
            <input
              type="number"
              min={1}
              max={10}
              value={form.maxReinforcementsPerHalfDay}
              onChange={(e) =>
                setForm({
                  ...form,
                  maxReinforcementsPerHalfDay: Math.min(10, Math.max(1, Number(e.target.value) || 1)),
                })
              }
              className="input w-24 text-center tabular-nums"
            />
          </div>
          <p className="text-[11px] text-slate-500 leading-relaxed flex-1 pb-2">
            Nombre de personnes que le moteur peut faire venir en renfort sur une même
            demi-journée. Au-delà, il s'arrête et signale les creux restants plutôt que de
            convoquer toute l'équipe. <strong>4 par défaut.</strong>
          </p>
        </div>
      </Section>

      <div className="flex justify-end mb-8">
        <Button variant="primary" icon={Save} loading={updateMut.isPending} disabled={!isDirty} onClick={handleSave}>
          {isDirty ? 'Enregistrer' : 'Aucun changement'}
        </Button>
      </div>

      <Section title="Code PIN équipe" description="À saisir sur les postes partagés pour le mode équipe." action={<Chip variant="slate"><Lock size={10} /> Sécurisé</Chip>}>
        <div className="flex items-end gap-3">
          <div className="flex-1">
            <label className="label">Nouveau code PIN (4 chiffres)</label>
            <input
              type="text"
              value={newPin}
              onChange={(e) => setNewPin(e.target.value.replace(/\D/g, '').slice(0, 4))}
              placeholder="••••"
              maxLength={4}
              className="input tabular-nums text-center text-[18px] font-semibold tracking-[0.3em]"
            />
          </div>
          <Button variant="primary" loading={pinMut.isPending} disabled={newPin.length !== 4} onClick={handlePinChange}>
            Modifier le PIN
          </Button>
        </div>
        {pinSuccess && (
          <div className="mt-3 text-[11.5px] text-primary flex items-center gap-1.5">
            <Check size={12} /> Code PIN modifié — effectif immédiatement sur tous les postes équipe.
          </div>
        )}
        <div className="mt-3 text-[11px] text-slate-500 bg-slate-50 border border-slate-200 rounded-lg p-3">
          L'ancien code cesse de fonctionner dès la modification. Communiquez le nouveau PIN à votre équipe.
        </div>
      </Section>

      <MultiSiteSection />
    </>
  );
}

/**
 * Début d'utilisation du logiciel par CETTE officine.
 *
 * « Il faut qu'on puisse mettre une date de début d'utilisation et que ça
 *   supprime tout ce qui est avant, parce que j'ai créé la pharmacie du Palais
 *   et ça me compte avant à l'infini. »
 *
 * Réglage à part du formulaire général, avec sa propre confirmation : il efface
 * des journées de planning, on ne le glisse pas dans un « Enregistrer » commun.
 */
function UsageStartSection() {
  const { data: pharmacy } = usePharmacySettings();
  const updateMut = useUpdatePharmacy();
  const current: string = pharmacy?.usageStartDate ? String(pharmacy.usageStartDate).slice(0, 10) : '';
  const [value, setValue] = useState(current);
  const [confirming, setConfirming] = useState(false);
  const [done, setDone] = useState<string | null>(null);

  useEffect(() => setValue(current), [current]);

  const dirty = value !== current;
  const frDate = (iso: string) => iso.split('-').reverse().join('/');

  async function apply() {
    setDone(null);
    const res = await updateMut.mutateAsync({ usageStartDate: value || null });
    setConfirming(false);
    setDone(
      value
        ? `Début d'utilisation au ${frDate(value)}. ${res.purged} journée${res.purged > 1 ? 's' : ''} de planning antérieure${res.purged > 1 ? 's' : ''} effacée${res.purged > 1 ? 's' : ''}.`
        : "Plus de date de début : l'historique est de nouveau compté."
    );
  }

  return (
    <Section
      title="Début d'utilisation du logiciel"
      description="Avant cette date, rien n'est planifié ni compté : balance des heures, fiche navette, comblement."
    >
      <div className="flex items-end gap-3">
        <div>
          <label className="label">Date de début</label>
          <input
            type="date"
            value={value}
            onChange={(e) => { setValue(e.target.value); setConfirming(false); setDone(null); }}
            className="input w-48"
          />
        </div>
        {!confirming && (
          <Button
            variant="primary"
            disabled={!dirty || updateMut.isPending}
            onClick={() => (value ? setConfirming(true) : void apply())}
          >
            {value ? 'Appliquer' : current ? 'Retirer la date' : 'Appliquer'}
          </Button>
        )}
      </div>

      <p className="text-[11px] text-slate-500 mt-2 leading-relaxed">
        Pour une officine qui démarre — ou qui vient d'être ajoutée au groupe. Sans date, le planning
        se remplit un mois en arrière et ces journées comptent comme des heures dues. Ce que vous
        deviez déjà à l'équipe ce jour-là se reprend dans le <strong>solde de départ</strong> de
        chaque salarié (Balance des heures).
      </p>

      {confirming && (
        <div className="mt-3 rounded-lg border border-[#b8741e]/30 bg-[#fcf1e2]/60 p-3 text-[12px] text-slate-700">
          <div className="flex items-start gap-2">
            <AlertCircle size={14} className="text-[#b8741e] mt-0.5 flex-shrink-0" />
            <div className="flex-1">
              Les journées de planning de <strong>{pharmacy?.name ?? 'cette officine'}</strong> antérieures
              au <strong>{frDate(value)}</strong> vont être <strong>effacées</strong> — sauf les semaines
              déjà validées. Les absences, les fiches des salariés et leurs dates d'embauche ne
              bougent pas.
              <div className="flex gap-2 mt-2.5">
                <Button variant="primary" size="sm" loading={updateMut.isPending} onClick={() => void apply()}>
                  Effacer et appliquer
                </Button>
                <Button variant="ghost" size="sm" onClick={() => setConfirming(false)}>Annuler</Button>
              </div>
            </div>
          </div>
        </div>
      )}

      {updateMut.error && (
        <div className="mt-3 text-[11.5px] text-danger">{(updateMut.error as Error).message}</div>
      )}
      {done && (
        <div className="mt-3 text-[11.5px] text-primary flex items-center gap-1.5">
          <Check size={12} /> {done}
        </div>
      )}
    </Section>
  );
}

/**
 * Multi-site (M9-3, lot 3A) — ajouter une officine au groupe.
 *
 * Il n'y a pas d'interrupteur « activer le multi-site » : ajouter une deuxième
 * pharmacie EST l'activation. Le sélecteur apparaît alors tout en bas de la
 * barre latérale.
 */
function MultiSiteSection() {
  const { data } = useSites();
  const createMut = useCreateSite();
  const [open, setOpen] = useState(false);
  const [form, setForm] = useState({ name: '', shuttleEmail: '', city: '', address: '', postalCode: '', teamPin: '' });
  const [error, setError] = useState<string | null>(null);

  const canSubmit =
    form.name.trim().length >= 2 &&
    /^[^@\s]+@[^@\s]+\.[^@\s]+$/.test(form.shuttleEmail) &&
    /^\d{4}$/.test(form.teamPin);

  const submit = () => {
    setError(null);
    createMut.mutate(
      {
        name: form.name.trim(),
        shuttleEmail: form.shuttleEmail.trim(),
        city: form.city.trim() || undefined,
        address: form.address.trim() || undefined,
        postalCode: form.postalCode.trim() || undefined,
        teamPin: form.teamPin,
      },
      {
        onSuccess: () => {
          setOpen(false);
          setForm({ name: '', shuttleEmail: '', city: '', address: '', postalCode: '', teamPin: '' });
        },
        onError: (e: any) => setError(e?.message ?? "La pharmacie n'a pas pu être créée."),
      }
    );
  };

  return (
    <Section
      title="Multi-site"
      description="Gérer plusieurs officines depuis ce compte. Chacune garde ses horaires, ses effectifs et son PIN équipe."
      action={
        !open ? (
          <Button variant="default" icon={Plus} onClick={() => setOpen(true)}>
            Ajouter une pharmacie
          </Button>
        ) : undefined
      }
    >
      <div className="space-y-2">
        {(data?.sites ?? []).map((s) => (
          <div key={s.id} className="flex items-center gap-2.5 px-3 py-2 rounded-lg border border-slate-200 bg-white">
            <Home size={14} className="text-slate-400 flex-shrink-0" />
            <div className="flex-1 min-w-0">
              <div className="text-[13px] font-medium text-slate-900 truncate">{s.name}</div>
              {s.city && <div className="text-[11px] text-slate-500">{s.city}</div>}
            </div>
            {s.id === data?.activeSiteId && (
              <span className="text-[10.5px] font-medium text-primary bg-primary-bg px-2 py-0.5 rounded-full">
                Site actif
              </span>
            )}
          </div>
        ))}
      </div>

      {!open && !data?.multiSite && (
        <div className="mt-3 text-[11px] text-slate-500 bg-slate-50 border border-slate-200 rounded-lg p-3">
          Une seule officine pour l'instant. Dès qu'une deuxième est ajoutée, un sélecteur apparaît en
          bas de la barre latérale pour passer de l'une à l'autre.
        </div>
      )}

      {open && (
        <div className="mt-4 border border-slate-200 rounded-xl p-4">
          <div className="text-[12.5px] font-medium text-slate-900 mb-3">Nouvelle officine</div>
          <div className="grid grid-cols-2 gap-4">
            <div>
              <label className="label">Nom de l'officine</label>
              <input value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} placeholder="Pharmacie du Palais" className="input" />
            </div>
            <div>
              <label className="label">Email destinataire de la fiche navette</label>
              <input type="email" value={form.shuttleEmail} onChange={(e) => setForm({ ...form, shuttleEmail: e.target.value })} placeholder="compta@exemple.fr" className="input" />
            </div>
            <div>
              <label className="label">Adresse <span className="text-slate-400 font-normal">(facultatif)</span></label>
              <input value={form.address} onChange={(e) => setForm({ ...form, address: e.target.value })} className="input" />
            </div>
            <div>
              <label className="label">Ville <span className="text-slate-400 font-normal">(facultatif)</span></label>
              <input value={form.city} onChange={(e) => setForm({ ...form, city: e.target.value })} className="input" />
            </div>
            <div>
              <label className="label">Code postal <span className="text-slate-400 font-normal">(facultatif)</span></label>
              <input value={form.postalCode} onChange={(e) => setForm({ ...form, postalCode: e.target.value })} placeholder="24000" className="input" />
            </div>
            <div>
              <label className="label">PIN équipe (4 chiffres)</label>
              <input value={form.teamPin} onChange={(e) => setForm({ ...form, teamPin: e.target.value.replace(/\D/g, '').slice(0, 4) })} placeholder="0000" inputMode="numeric" className="input" />
            </div>
          </div>
          <div className="mt-3 text-[11px] text-slate-500 bg-slate-50 border border-slate-200 rounded-lg p-3">
            Cette officine démarre vide : ses horaires d'ouverture, ses effectifs requis et ses
            salariés se saisissent une fois qu'on a basculé dessus. Son PIN équipe lui est propre —
            le poste comptoir de l'une n'ouvre pas la journée de l'autre.
          </div>
          {error && <div className="mt-3 text-[11.5px] text-red-600">{error}</div>}
          <div className="mt-3 flex items-center gap-2">
            <Button onClick={submit} loading={createMut.isPending} disabled={!canSubmit}>
              Créer la pharmacie
            </Button>
            <Button variant="ghost" onClick={() => { setOpen(false); setError(null); }}>
              Annuler
            </Button>
          </div>
        </div>
      )}
    </Section>
  );
}

// ============================================================================
// 2. OPENING HOURS
// ============================================================================

function OpeningHoursSection() {
  const { data: pharmacy, isLoading } = usePharmacySettings();
  const updateMut = useUpdateOpeningHours();

  const [hours, setHours] = useState<any[]>([]);

  useEffect(() => {
    if (pharmacy?.openingHours) {
      // Assurer les 7 jours
      const existing = new Map(pharmacy.openingHours.map((h: any) => [h.dayOfWeek, h]));
      const full = [1, 2, 3, 4, 5, 6, 0].map((d) => existing.get(d) ?? {
        dayOfWeek: d,
        morningOpen: d === 0 ? null : '08:30',
        morningClose: d === 0 ? null : '12:30',
        afternoonOpen: d === 0 || d === 6 ? null : '14:00',
        afternoonClose: d === 0 || d === 6 ? null : '19:30',
        closed: d === 0,
      });
      setHours(full);
    }
  }, [pharmacy]);

  // Changer les horaires déplace les creux de couverture partout : on propose
  // un recalcul général du personnel en horaires variables (demande du client).
  const [recompute, setRecompute] = useState<{
    from: string;
    to: string;
    staff: VariableStaffMember[];
  } | null>(null);

  async function handleSave() {
    const res = await updateMut.mutateAsync(hours);
    if (res?.suggestVariableRecompute) {
      setRecompute({
        from: String(res.recomputeRange.from).slice(0, 10),
        to: String(res.recomputeRange.to).slice(0, 10),
        staff: res.variableStaff,
      });
    }
  }

  function updateDay(index: number, field: string, value: any) {
    const next = [...hours];
    next[index] = { ...next[index], [field]: value };
    setHours(next);
  }

  if (isLoading) return <div className="text-sm text-slate-500">Chargement…</div>;

  const res = updateMut.data as { adjustedSlots?: number; affectedEmployees?: number; affectedNames?: string[] } | undefined;
  const nSlots = res?.adjustedSlots ?? 0;
  const nEmp = res?.affectedEmployees ?? 0;
  const affectedNames = res?.affectedNames ?? [];
  const openingSuccessMsg = nSlots > 0
    ? `Horaires enregistrés — ${nSlots} créneau${nSlots > 1 ? 'x' : ''} d'emploi du temps ajusté${nSlots > 1 ? 's' : ''} aux nouveaux horaires${nEmp > 0 ? ` (${nEmp} salarié${nEmp > 1 ? 's' : ''} concerné${nEmp > 1 ? 's' : ''})` : ''}.`
    : 'Horaires enregistrés. Aucun emploi du temps à ajuster.';

  return (
    <>
      <SaveBanner isPending={updateMut.isPending} error={updateMut.error as Error | null} success={updateMut.isSuccess} successMessage={openingSuccessMsg} />
      {updateMut.isSuccess && affectedNames.length > 0 && (
        <div className="-mt-2 mb-4 flex flex-wrap gap-1.5">
          {affectedNames.map((n) => (
            <span key={n} className="inline-flex items-center rounded-full bg-primary-bg border border-primary/30 px-2 py-0.5 text-[11px] text-primary">
              {n}
            </span>
          ))}
        </div>
      )}

      <Section title="Horaires hebdomadaires" description="Le moteur d'organisation se base sur ces horaires pour calculer la couverture nécessaire.">
        <div className="space-y-2">
          {hours.map((h, idx) => (
            <div key={h.dayOfWeek} className={`border rounded-lg p-3 ${h.closed ? 'bg-slate-50 border-slate-200' : 'bg-white border-slate-200'}`}>
              <div className="grid grid-cols-[120px_1fr_auto] gap-3 items-center">
                <div className="flex items-center gap-2">
                  <div className="text-[12.5px] font-semibold">{DAYS_FR[h.dayOfWeek]}</div>
                </div>

                {h.closed ? (
                  <div className="text-[11.5px] text-slate-500 italic">Fermé</div>
                ) : (
                  <div className="grid grid-cols-2 gap-3">
                    <div className="flex items-center gap-1.5">
                      <input type="time" value={h.morningOpen ?? ''} onChange={(e) => updateDay(idx, 'morningOpen', e.target.value || null)} className="input text-[11px] py-1 tabular-nums" />
                      <span className="text-[10px] text-slate-400">→</span>
                      <input type="time" value={h.morningClose ?? ''} onChange={(e) => updateDay(idx, 'morningClose', e.target.value || null)} className="input text-[11px] py-1 tabular-nums" />
                    </div>
                    <div className="flex items-center gap-1.5">
                      <input type="time" value={h.afternoonOpen ?? ''} onChange={(e) => updateDay(idx, 'afternoonOpen', e.target.value || null)} className="input text-[11px] py-1 tabular-nums" />
                      <span className="text-[10px] text-slate-400">→</span>
                      <input type="time" value={h.afternoonClose ?? ''} onChange={(e) => updateDay(idx, 'afternoonClose', e.target.value || null)} className="input text-[11px] py-1 tabular-nums" />
                    </div>
                  </div>
                )}

                <label className="flex items-center gap-1.5 text-[11px] cursor-pointer whitespace-nowrap">
                  <input type="checkbox" checked={h.closed} onChange={(e) => updateDay(idx, 'closed', e.target.checked)} className="rounded" />
                  Fermé
                </label>
              </div>
            </div>
          ))}
        </div>

        <div className="mt-3 text-[11px] text-slate-500">
          <strong>Matin</strong> : ouverture → fermeture du matin. <strong>Après-midi</strong> : reprise → fermeture du soir. Laisser vide pour journée continue.
        </div>
      </Section>

      <div className="flex justify-end">
        <Button variant="primary" icon={Save} loading={updateMut.isPending} onClick={handleSave}>
          Enregistrer les horaires
        </Button>
      </div>

      {/* Les gardes vivent ici : une garde est une ouverture exceptionnelle, et
          c'est là qu'on vient régler quand l'officine est ouverte. Elles ont leur
          propre enregistrement, sans rapport avec le bouton ci-dessus. */}
      <DutySection />

      {recompute && (
        <VariableFillModal
          from={recompute.from}
          to={recompute.to}
          staff={recompute.staff}
          title="Recalculer les horaires du personnel variable ?"
          reason="Les horaires d'ouverture ont changé : les besoins de couverture ont bougé sur toute la période à venir."
          onClose={() => setRecompute(null)}
        />
      )}
    </>
  );
}

// ============================================================================
// 3. STAFFING
// ============================================================================

function StaffingSection() {
  const { data: pharmacy, isLoading } = usePharmacySettings();
  const updateMut = useUpdateStaffingRequirements();

  const [reqs, setReqs] = useState<any[]>([]);

  useEffect(() => {
    if (pharmacy?.staffingRequirements) {
      // Assurer qu'on a les 11 missions, même celles non configurées
      const existing = new Map(pharmacy.staffingRequirements.map((r: any) => [r.mission, r]));
      setReqs(MISSIONS.map((m) => existing.get(m.v) ?? {
        mission: m.v,
        minCount: 0,
        targetCount: 0,
        positionCounts: null,
        appliesMorning: true,
        appliesAfternoon: true,
        activeFromMonth: null,
        activeToMonth: null,
        notes: null,
      }));
    }
  }, [pharmacy]);

  function update(idx: number, field: string, value: any) {
    const next = [...reqs];
    next[idx] = { ...next[idx], [field]: value };
    setReqs(next);
  }

  // Détail par poste (missions de présence) : met à jour un poste et recalcule le
  // total (targetCount = minCount = somme des postes) pour rester cohérent avec le moteur.
  function updatePos(idx: number, pos: string, value: number) {
    const next = [...reqs];
    const pc = { ...(next[idx].positionCounts ?? {}) };
    if (value > 0) pc[pos] = value;
    else delete pc[pos];
    const total = Object.values(pc).reduce((s: number, n: any) => s + (Number(n) || 0), 0);
    next[idx] = { ...next[idx], positionCounts: Object.keys(pc).length ? pc : null, targetCount: total, minCount: total };
    setReqs(next);
  }

  async function handleSave() {
    await updateMut.mutateAsync(reqs);
  }

  if (isLoading) return <div className="text-sm text-slate-500">Chargement…</div>;

  const months = ['Jan', 'Fév', 'Mar', 'Avr', 'Mai', 'Jui', 'Jul', 'Aoû', 'Sep', 'Oct', 'Nov', 'Déc'];

  return (
    <>
      <SaveBanner isPending={updateMut.isPending} error={updateMut.error as Error | null} success={updateMut.isSuccess} />

      <Section title="Besoins en effectifs par mission" description="Le moteur d'organisation vérifie le couvertage pour chaque mission activée.">
        <div className="space-y-3">
          {reqs.map((r, idx) => {
            const mission = MISSIONS.find((m) => m.v === r.mission);
            const isPresence = PRESENCE_MISSIONS.has(r.mission);
            const isActive = r.targetCount > 0;
            return (
              <div key={r.mission} className={`border rounded-lg p-3 transition-colors ${isActive ? 'bg-white border-slate-200' : 'bg-slate-50/50 border-slate-200'}`}>
                {isPresence ? (
                  // Effectif requis DÉTAILLÉ PAR POSTE (R11) — ex. Ouverture = 1 pharmacien + 1 préparateur.
                  <div className="grid grid-cols-[1fr_auto] gap-3 items-start">
                    <div>
                      <div className="text-[12.5px] font-semibold">{mission?.l}</div>
                      <div className="text-[10.5px] text-slate-500 mt-0.5">{mission?.d} · nombre requis par poste</div>
                      <div className="flex flex-wrap gap-x-4 gap-y-1.5 mt-2">
                        {STAFFING_POSITIONS.map((p) => (
                          <div key={p.v} className="flex items-center gap-1.5">
                            <label className="text-[11px] text-slate-600 whitespace-nowrap">{p.l}</label>
                            <input
                              type="number"
                              min={0}
                              value={r.positionCounts?.[p.v] ?? 0}
                              onChange={(e) => updatePos(idx, p.v, parseInt(e.target.value) || 0)}
                              className="input w-14 text-center tabular-nums py-1 text-[12px]"
                            />
                          </div>
                        ))}
                        <div className="text-[10.5px] text-slate-400 self-center">Total : <strong className="tabular-nums text-slate-600">{r.targetCount}</strong></div>
                      </div>
                    </div>
                    <div className="flex items-center gap-1.5">
                      <label className="flex items-center gap-1 text-[10.5px] cursor-pointer">
                        <input type="checkbox" checked={r.appliesMorning} onChange={(e) => update(idx, 'appliesMorning', e.target.checked)} className="rounded" />
                        Matin
                      </label>
                      <label className="flex items-center gap-1 text-[10.5px] cursor-pointer">
                        <input type="checkbox" checked={r.appliesAfternoon} onChange={(e) => update(idx, 'appliesAfternoon', e.target.checked)} className="rounded" />
                        A-M
                      </label>
                    </div>
                  </div>
                ) : (
                <div className="grid grid-cols-[1fr_auto_auto_auto] gap-3 items-start">
                  <div>
                    <div className="text-[12.5px] font-semibold">{mission?.l}</div>
                    <div className="text-[10.5px] text-slate-500 mt-0.5">{mission?.d}</div>
                  </div>

                  <div className="flex items-center gap-2">
                    <label className="text-[10.5px] text-slate-600 whitespace-nowrap">Min</label>
                    <input type="number" min={0} value={r.minCount} onChange={(e) => update(idx, 'minCount', parseInt(e.target.value) || 0)} className="input w-14 text-center tabular-nums py-1 text-[12px]" />
                  </div>

                  <div className="flex items-center gap-2">
                    <label className="text-[10.5px] text-slate-600 whitespace-nowrap">Cible</label>
                    <input type="number" min={0} value={r.targetCount} onChange={(e) => update(idx, 'targetCount', parseInt(e.target.value) || 0)} className="input w-14 text-center tabular-nums py-1 text-[12px]" />
                  </div>

                  <div className="flex items-center gap-1.5">
                    <label className="flex items-center gap-1 text-[10.5px] cursor-pointer">
                      <input type="checkbox" checked={r.appliesMorning} onChange={(e) => update(idx, 'appliesMorning', e.target.checked)} className="rounded" />
                      Matin
                    </label>
                    <label className="flex items-center gap-1 text-[10.5px] cursor-pointer">
                      <input type="checkbox" checked={r.appliesAfternoon} onChange={(e) => update(idx, 'appliesAfternoon', e.target.checked)} className="rounded" />
                      A-M
                    </label>
                  </div>
                </div>
                )}

                {isActive && (
                  <div className="mt-3 pt-3 border-t border-slate-100 flex items-center gap-3">
                    <label className="text-[10.5px] text-slate-600">Période active :</label>
                    <select
                      value={r.activeFromMonth ?? ''}
                      onChange={(e) => update(idx, 'activeFromMonth', e.target.value ? parseInt(e.target.value) : null)}
                      className="input text-[11px] py-1 w-24"
                    >
                      <option value="">Toute l'année</option>
                      {months.map((m, i) => <option key={i} value={i + 1}>{m}</option>)}
                    </select>
                    {r.activeFromMonth && (
                      <>
                        <span className="text-[10.5px] text-slate-500">→</span>
                        <select
                          value={r.activeToMonth ?? ''}
                          onChange={(e) => update(idx, 'activeToMonth', e.target.value ? parseInt(e.target.value) : null)}
                          className="input text-[11px] py-1 w-24"
                        >
                          <option value="">Fin d'année</option>
                          {months.map((m, i) => <option key={i} value={i + 1}>{m}</option>)}
                        </select>
                      </>
                    )}
                    {r.activeFromMonth && (
                      <div className="text-[10px] text-slate-400 italic">
                        Ex: vaccination oct→fév
                      </div>
                    )}
                  </div>
                )}
              </div>
            );
          })}
        </div>

        <div className="mt-4 bg-slate-50 border border-slate-200 rounded-lg p-3 text-[11px] text-slate-600">
          <strong>Min</strong> = effectif minimum requis (seuil d'alerte). <strong>Cible</strong> = effectif idéal. Si un champ est à 0, la mission est désactivée.
          Pour l'<strong>ouverture / fermeture</strong>, l'effectif se règle <strong>par poste</strong> (ex. 1 pharmacien + 1 préparateur) : le moteur alerte si un poste requis n'est pas présent.
        </div>
      </Section>

      <div className="flex justify-end">
        <Button variant="primary" icon={Save} loading={updateMut.isPending} onClick={handleSave}>
          Enregistrer les effectifs
        </Button>
      </div>
    </>
  );
}

// ============================================================================
// 4. HOLIDAYS
// ============================================================================

function HolidaysSection() {
  const { data: holidays, isLoading } = useHolidays();
  const seedMut = useSeedFrenchHolidays();
  const updateMut = useUpdateHoliday();
  const deleteMut = useDeleteHoliday();
  const createMut = useCreateHoliday();

  const [showCustomForm, setShowCustomForm] = useState(false);
  const [custom, setCustom] = useState({ date: '', name: '', isOpen: false, payMultiplier: 2.0 });
  const [year, setYear] = useState(new Date().getFullYear());

  async function handleSeed() {
    await seedMut.mutateAsync(year);
  }

  async function handleAddCustom() {
    if (!custom.date || !custom.name) return;
    await createMut.mutateAsync({ ...custom });
    setCustom({ date: '', name: '', isOpen: false, payMultiplier: 2.0 });
    setShowCustomForm(false);
  }

  if (isLoading) return <div className="text-sm text-slate-500">Chargement…</div>;

  return (
    <>
      <Section
        title="Jours fériés français"
        description="Importez les 11 jours fériés légaux. L'officine y est fermée par défaut : choisissez « Ouvert » pour les jours où vous ouvrez."
        action={
          <div className="flex items-center gap-2">
            <input
              type="number"
              min={2020}
              max={2100}
              value={year}
              onChange={(e) => setYear(parseInt(e.target.value) || new Date().getFullYear())}
              className="input w-24 text-center tabular-nums py-1.5"
            />
            <Button variant="primary" icon={Sparkles} size="sm" loading={seedMut.isPending} onClick={handleSeed}>
              Importer {year}
            </Button>
          </div>
        }
      >
        {seedMut.isSuccess && seedMut.data && (
          <div className="bg-primary-bg border border-primary/30 rounded-lg p-3 mb-3 text-[11.5px] text-primary">
            {seedMut.data.createdCount > 0 ? `${seedMut.data.createdCount} jour(s) férié(s) ajouté(s)` : 'Tous les jours fériés de cette année sont déjà enregistrés'}
          </div>
        )}

        {holidays && holidays.length > 0 ? (
          <>
          <div className="border border-slate-200 rounded-lg overflow-hidden">
            {/* Colonnes de largeur FIXE : chaque ligne est sa propre grille, des
                colonnes `auto` ne s'alignaient pas sur l'en-tête (« OUVERT
                ?MAJORATION » collés, retour du boss du 28/10). */}
            <div className={`${HOLIDAY_COLS} px-3 py-2 bg-slate-50 border-b border-slate-200 text-[10px] uppercase tracking-wider text-slate-500 font-medium`}>
              <div>Date</div>
              <div>Férié</div>
              <div>Ce jour-là</div>
              <div></div>
            </div>
            {holidays.map((h: any) => (
              <HolidayRow
                key={h.id}
                holiday={h}
                onUpdate={(data) => updateMut.mutateAsync({ id: h.id, ...data })}
                onDelete={() => {
                  if (window.confirm(`Supprimer le jour férié "${h.name}" ?`)) deleteMut.mutateAsync(h.id);
                }}
              />
            ))}
          </div>
          <div className="mt-2 text-[11px] text-slate-500 leading-relaxed">
            <strong>Fermé</strong> : la journée est grisée au planning et il n'y a rien à organiser.
            Un salarié qui y est planifié garde ses heures. <strong>Ouvert</strong> : journée normale.
            La majoration est notée pour information : la fiche navette ne la calcule pas.
          </div>
          </>
        ) : (
          <div className="text-center py-8 text-[12px] text-slate-500">
            Aucun jour férié enregistré. Cliquez sur "Importer {year}" pour pré-remplir automatiquement.
          </div>
        )}
      </Section>

      <Section
        title="Jour férié personnalisé"
        description="Ajoutez une fermeture exceptionnelle (pont, événement familial, etc.)."
        action={
          !showCustomForm ? (
            <Button variant="default" icon={Plus} size="sm" onClick={() => setShowCustomForm(true)}>Ajouter</Button>
          ) : (
            <Button variant="ghost" icon={X} size="sm" onClick={() => setShowCustomForm(false)}>Annuler</Button>
          )
        }
      >
        {showCustomForm ? (
          <div className="space-y-3">
            <div className="grid grid-cols-[150px_1fr] gap-3">
              <div>
                <label className="label">Date</label>
                <input type="date" value={custom.date} onChange={(e) => setCustom({ ...custom, date: e.target.value })} className="input" />
              </div>
              <div>
                <label className="label">Nom</label>
                <input value={custom.name} onChange={(e) => setCustom({ ...custom, name: e.target.value })} placeholder="Ex: Pont du 14 mai" className="input" />
              </div>
            </div>
            <div className="flex flex-wrap items-center gap-3">
              <span className="text-[11.5px] text-slate-600">Ce jour-là</span>
              <OpenClosedToggle isOpen={custom.isOpen} onChange={(v) => setCustom({ ...custom, isOpen: v })} />
              {custom.isOpen && (
                <MajorationField
                  multiplier={custom.payMultiplier}
                  onCommit={(m) => setCustom({ ...custom, payMultiplier: m })}
                />
              )}
            </div>
            <Button variant="primary" icon={Plus} onClick={handleAddCustom} disabled={!custom.date || !custom.name}>
              Créer le jour férié
            </Button>
          </div>
        ) : (
          <div className="text-[11.5px] text-slate-500">
            Cliquez sur "Ajouter" pour créer un jour férié qui n'est pas dans la liste légale.
          </div>
        )}
      </Section>
    </>
  );
}

/** Colonnes du tableau des fériés : date · nom · « Ce jour-là » · corbeille. */
const HOLIDAY_COLS = 'grid grid-cols-[140px_1fr_340px_32px] gap-3';

/**
 * « Fermé / Ouvert » pour un férié. Retour du boss (28/10) : une case « Ouvert ?
 * Non » se lisait à l'envers. Deux choix nommés ne se lisent que dans un sens.
 * Gris pour fermé, ambre pour ouvert : les couleurs du planning.
 */
function OpenClosedToggle({ isOpen, onChange }: { isOpen: boolean; onChange: (v: boolean) => void }) {
  return (
    <div className="inline-flex bg-slate-100 rounded-lg p-0.5" role="radiogroup" aria-label="Ce jour-là">
      {[{ v: false, l: 'Fermé' }, { v: true, l: 'Ouvert' }].map((o) => {
        const active = isOpen === o.v;
        return (
          <button
            key={o.l}
            type="button"
            role="radio"
            aria-checked={active}
            onClick={() => { if (!active) onChange(o.v); }}
            className={`px-3 py-1 rounded-md text-[11.5px] font-medium transition-colors ${
              active
                ? o.v ? 'bg-amber-50 text-amber-800 border border-amber-300 shadow-sm' : 'bg-white text-slate-700 border border-slate-300 shadow-sm'
                : 'text-slate-500 hover:text-slate-800 border border-transparent'
            }`}
          >
            {o.l}
          </button>
        );
      })}
    </div>
  );
}

/**
 * La majoration s'enregistre en multiplicateur (2 = heures payées double) ;
 * on la montre en pourcentage, « 2 » tout seul ne disait rien. Enregistrée à la
 * sortie du champ, pas à chaque chiffre tapé.
 */
const toPct = (multiplier: number) => Math.round((multiplier - 1) * 100);

function MajorationField({ multiplier, onCommit }: { multiplier: number; onCommit: (m: number) => void }) {
  const [pct, setPct] = useState(String(toPct(multiplier)));
  useEffect(() => { setPct(String(toPct(multiplier))); }, [multiplier]);
  const commit = () => {
    const n = Math.max(0, parseFloat(pct.replace(',', '.')) || 0);
    setPct(String(n));
    if (1 + n / 100 !== multiplier) onCommit(1 + n / 100);
  };
  return (
    <label className="flex items-center gap-1.5 text-[11.5px] text-slate-600 whitespace-nowrap">
      heures majorées de
      <input
        type="number"
        min={0}
        step={25}
        value={pct}
        onChange={(e) => setPct(e.target.value)}
        onBlur={commit}
        onKeyDown={(e) => { if (e.key === 'Enter') (e.target as HTMLInputElement).blur(); }}
        className="input w-16 text-center text-[11.5px] py-0.5 tabular-nums"
      />
      %
    </label>
  );
}

function HolidayRow({ holiday, onUpdate, onDelete }: { holiday: any; onUpdate: (data: any) => Promise<any>; onDelete: () => void }) {
  const [isOpen, setIsOpen] = useState(holiday.isOpen);
  const [multiplier, setMultiplier] = useState(holiday.payMultiplier);

  const toggleOpen = async (value: boolean) => {
    setIsOpen(value);
    await onUpdate({ isOpen: value });
  };

  const handleMultiplier = async (value: number) => {
    setMultiplier(value);
    await onUpdate({ payMultiplier: value });
  };

  const date = new Date(holiday.date);
  const dateLabel = date.toLocaleDateString('fr-FR', { day: 'numeric', month: 'long', year: 'numeric' });

  return (
    <div className={`${HOLIDAY_COLS} px-3 py-2 border-b border-slate-100 last:border-b-0 items-center hover:bg-slate-50/50`}>
      <div className="text-[11.5px] text-slate-600 tabular-nums">{dateLabel}</div>
      <div className="text-[12px] font-medium text-slate-900 truncate">{holiday.name}</div>
      <div className="flex items-center gap-3">
        <OpenClosedToggle isOpen={isOpen} onChange={toggleOpen} />
        {isOpen && <MajorationField multiplier={multiplier} onCommit={handleMultiplier} />}
      </div>
      <button onClick={onDelete} title="Supprimer ce jour férié" className="p-1 hover:bg-danger-bg rounded text-slate-400 hover:text-danger justify-self-end">
        <Trash2 size={13} />
      </button>
    </div>
  );
}

// ============================================================================
// 5. PRIMES
// ============================================================================

function PrimesSection() {
  const { data: primes, isLoading } = usePrimes();
  const createMut = useCreatePrime();
  const updateMut = useUpdatePrime();
  const deleteMut = useDeletePrime();

  const [showForm, setShowForm] = useState(false);
  const [form, setForm] = useState({ name: '', amount: '', amountFormula: '', periodicity: 'monthly' as 'monthly' | 'annual' | 'per_event', active: true });

  async function handleCreate() {
    if (!form.name) return;
    await createMut.mutateAsync({
      name: form.name,
      amount: form.amount ? parseFloat(form.amount) : null,
      amountFormula: form.amountFormula || null,
      periodicity: form.periodicity,
      active: form.active,
    });
    setForm({ name: '', amount: '', amountFormula: '', periodicity: 'monthly', active: true });
    setShowForm(false);
  }

  const periodicityLabels: Record<string, string> = {
    monthly: 'Mensuelle', annual: 'Annuelle', per_event: 'Par événement',
  };

  if (isLoading) return <div className="text-sm text-slate-500">Chargement…</div>;

  return (
    <>
      <Section
        title="Primes et règles de rémunération"
        description="Créez les primes paramétrables qui apparaîtront sur la fiche navette mensuelle."
        action={
          !showForm ? (
            <Button variant="primary" icon={Plus} size="sm" onClick={() => setShowForm(true)}>Nouvelle prime</Button>
          ) : (
            <Button variant="ghost" icon={X} size="sm" onClick={() => setShowForm(false)}>Annuler</Button>
          )
        }
      >
        {showForm && (
          <div className="bg-slate-50 border border-slate-200 rounded-lg p-4 mb-4 space-y-3">
            <div className="grid grid-cols-2 gap-3">
              <div>
                <label className="label">Nom de la prime</label>
                <input value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} placeholder="Ex: Prime de fin d'année" className="input" />
              </div>
              <div>
                <label className="label">Périodicité</label>
                <select value={form.periodicity} onChange={(e) => setForm({ ...form, periodicity: e.target.value as any })} className="input">
                  <option value="monthly">Mensuelle</option>
                  <option value="annual">Annuelle</option>
                  <option value="per_event">Par événement</option>
                </select>
              </div>
            </div>
            <div className="grid grid-cols-2 gap-3">
              <div>
                <label className="label">Montant fixe (€) <span className="text-slate-400 font-normal">(facultatif)</span></label>
                <input type="number" step="0.01" value={form.amount} onChange={(e) => setForm({ ...form, amount: e.target.value })} placeholder="80" className="input tabular-nums" />
              </div>
              <div>
                <label className="label">Formule de calcul <span className="text-slate-400 font-normal">(facultatif)</span></label>
                <input value={form.amountFormula} onChange={(e) => setForm({ ...form, amountFormula: e.target.value })} placeholder="10 %, 50 % abonnement…" className="input" />
              </div>
            </div>
            <Button variant="primary" icon={Plus} onClick={handleCreate} disabled={!form.name} loading={createMut.isPending}>
              Créer la prime
            </Button>
          </div>
        )}

        {primes && primes.length > 0 ? (
          <div className="space-y-2">
            {primes.map((p: any) => (
              <div key={p.id} className="border border-slate-200 rounded-lg p-3 bg-white">
                <div className="flex items-start justify-between gap-3">
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center gap-2">
                      <div className="text-[13px] font-semibold">{p.name}</div>
                      <Chip variant={p.active ? 'green' : 'slate'} size="sm">
                        {p.active ? 'Active' : 'Inactive'}
                      </Chip>
                      <Chip variant="default" size="sm">{periodicityLabels[p.periodicity]}</Chip>
                    </div>
                    <div className="text-[11px] text-slate-500 mt-1">
                      {p.amount !== null && `${p.amount} € · `}
                      {p.amountFormula}
                      {!p.amount && !p.amountFormula && 'Montant libre'}
                    </div>
                  </div>
                  <div className="flex items-center gap-1">
                    <Button
                      variant="ghost"
                      size="sm"
                      onClick={() => updateMut.mutateAsync({ id: p.id, active: !p.active })}
                    >
                      {p.active ? 'Désactiver' : 'Activer'}
                    </Button>
                    <button
                      onClick={() => { if (window.confirm(`Supprimer la prime "${p.name}" ?`)) deleteMut.mutateAsync(p.id); }}
                      className="p-1.5 rounded hover:bg-danger-bg text-slate-400 hover:text-danger"
                    >
                      <Trash2 size={13} />
                    </button>
                  </div>
                </div>
              </div>
            ))}
          </div>
        ) : !showForm && (
          <div className="text-center py-8 text-[12px] text-slate-500">
            Aucune prime configurée. Cliquez sur "Nouvelle prime" pour commencer.
          </div>
        )}
      </Section>

      <div className="bg-slate-50 border border-slate-200 rounded-lg p-4 text-[11.5px] text-slate-600">
        <strong>À venir :</strong> affectation des primes à certains salariés uniquement, calcul automatique des indemnités kilométriques, prise en charge des tickets restaurant.
      </div>
    </>
  );
}

// ============================================================================
// Poste comptoir
// ============================================================================
/**
 * Ouvre CET ordinateur à toute l'équipe.
 *
 * Le geste est franc et sans retour immédiat : la session du titulaire est
 * fermée sur cette machine, qui repart aussitôt sur les trois écrans partagés.
 * C'est voulu — un poste comptoir qui garde une session pleine « au cas où »
 * n'est pas un poste comptoir.
 */
function CounterPostSection() {
  const openCounter = useOpenCounter();
  const [confirming, setConfirming] = useState(false);
  const alreadyPost = counter.isPost();

  return (
    <Section
      title="Poste comptoir"
      description="Un ordinateur de l'officine, ouvert en permanence sur trois écrans"
    >

      <div className="bg-slate-50 border border-slate-200 rounded-xl p-4 text-[12.5px] text-slate-600 leading-relaxed space-y-2 mb-4">
        <p>
          Sur un poste comptoir, l'équipe voit <strong>l'emploi du temps</strong>,{' '}
          <strong>l'organisation du jour</strong> et <strong>les rendez-vous</strong> sans rien
          saisir — l'écran est ouvert, en permanence. Elle peut <strong>prendre et annuler des
          rendez-vous</strong> ; tout le reste est en consultation.
        </p>
        <p>
          Le reste du logiciel — salariés, heures, validation, fiche navette, réglages —{' '}
          <strong>n'est pas atteignable</strong> depuis ce poste. Vous le rouvrez quand vous voulez
          avec votre <strong>code PIN</strong>, sur ce même écran, et il se referme seul au bout de
          30 minutes ou après 5 minutes sans activité.
        </p>
        <p className="text-slate-500">
          Les <strong>motifs d'absence</strong> ne sont jamais affichés au comptoir : le planning y
          indique « absent », rien de plus.
        </p>
      </div>

      <div className="bg-amber-50 border border-amber-200 rounded-xl p-3.5 text-[12px] text-amber-800 leading-relaxed mb-4">
        Votre code PIN devient la clé de la gestion depuis ce poste. Si c'est encore celui d'origine,
        changez-le dans <strong>Pharmacie → Code PIN de l'équipe</strong> avant d'installer un poste
        comptoir.
      </div>

      {alreadyPost ? (
        <div>
          <div className="text-[12.5px] text-slate-600">
            Cet ordinateur <strong>est déjà un poste comptoir</strong> — vous y êtes en session de
            gestion, ouverte avec le PIN. Utilisez « Verrouiller l'écran », en bas de la barre
            latérale, pour le rendre à l'équipe.
          </div>
          {/* La seule sortie. Sans ce bouton, une machine reste un poste comptoir
              pour toujours : Clerk n'y démarre plus, et le PIN ne fait qu'ouvrir
              la gestion — il ne rend pas l'ordinateur à un usage normal. */}
          <button
            onClick={() => {
              if (!window.confirm("Rendre cet ordinateur à un usage normal ? Le mode comptoir sera retiré et il faudra se reconnecter."))
                return;
              counter.uninstall();
              auth.clearToken();
              window.location.assign('/login');
            }}
            className="mt-4 text-[13px] font-medium text-slate-700 border border-slate-300 rounded-lg px-4 py-2 hover:bg-slate-100 transition-colors"
          >
            Retirer le mode comptoir de cet ordinateur
          </button>
        </div>
      ) : !confirming ? (
        <button
          onClick={() => setConfirming(true)}
          className="text-[13px] font-medium text-primary border border-primary/40 rounded-lg px-4 py-2 hover:bg-primary-bg transition-colors"
        >
          Faire de cet ordinateur un poste comptoir
        </button>
      ) : (
        <div className="border border-primary/40 rounded-xl p-4">
          <div className="text-[12.5px] text-slate-700 leading-relaxed mb-3">
            Votre session va se fermer sur cet ordinateur, qui basculera aussitôt en mode comptoir.
            Vous y reviendrez avec votre code PIN. Vos autres appareils ne sont pas touchés.
          </div>
          <div className="flex gap-2">
            <button
              onClick={() => openCounter.mutate()}
              disabled={openCounter.isPending}
              className="text-[13px] font-medium text-white bg-primary rounded-lg px-4 py-2 disabled:opacity-40 hover:opacity-90 transition-opacity"
            >
              {openCounter.isPending ? 'Installation…' : 'Installer le poste comptoir'}
            </button>
            <button
              onClick={() => setConfirming(false)}
              className="text-[13px] text-slate-600 px-4 py-2 hover:bg-slate-100 rounded-lg"
            >
              Annuler
            </button>
          </div>
          {openCounter.isError && (
            <div className="mt-3 text-[12px] text-danger">
              Installation impossible : {(openCounter.error as Error).message}
            </div>
          )}
        </div>
      )}
    </Section>
  );
}

// ============================================================================
// Accès en consultation
// ============================================================================
/**
 * Ouvre le RH de l'officine active, en lecture, à une adresse PharmaGestion.
 *
 * Le cas d'origine : un titulaire gère le RH de deux officines, mais la compta
 * et les remises d'une seule. Le titulaire de la seconde a déjà son compte
 * PharmaGestion ; il veut voir le planning de son officine, sans le modifier.
 * Il voit exactement ce que voit le poste comptoir — et rien d'autre.
 */
function ViewerAccessSection() {
  const { data: pharmacy } = usePharmacySettings();
  const { data: viewers, isLoading } = useViewers();
  const add = useAddViewer();
  const remove = useRemoveViewer();
  const [email, setEmail] = useState('');
  // Le refus le plus fréquent : le titulaire est passé sur PharmaRH avant qu'on
  // lui ouvre l'accès, et y a créé sa propre pharmacie. Il a besoin d'un mode
  // d'emploi, pas d'une ligne rouge.
  const alreadyTitulaire =
    add.error instanceof ApiError &&
    (add.error.body as { code?: string } | null)?.code === 'ADDRESS_IS_TITULAIRE';

  function handleAdd() {
    const value = email.trim();
    if (!value) return;
    add.mutate(value, { onSuccess: () => setEmail('') });
  }

  function handleRemove(id: string, address: string) {
    if (!window.confirm(`Retirer l'accès de ${address} ? Il ne verra plus le RH de cette officine.`)) return;
    remove.mutate(id);
  }

  return (
    <Section
      title="Accès en consultation"
      description={`Laisser un titulaire voir le RH de ${pharmacy?.name ?? 'cette officine'}, sans rien pouvoir y modifier`}
    >
      <div className="bg-slate-50 border border-slate-200 rounded-xl p-4 text-[12.5px] text-slate-600 leading-relaxed space-y-2 mb-4">
        <p>
          Pour le titulaire d'une officine dont <strong>vous gérez le RH</strong> : il a déjà son compte
          PharmaGestion (compta, remises) et veut suivre son équipe. Il voit ce que voit le{' '}
          <strong>poste comptoir</strong> : <strong>l'emploi du temps</strong>,{' '}
          <strong>l'organisation du jour</strong> et <strong>les rendez-vous</strong>.
        </p>
        <p>
          Salariés, heures, validation, fiche navette et réglages lui restent{' '}
          <strong>fermés</strong>, et il ne peut pas déverrouiller la gestion. Comme au comptoir, il
          peut prendre et annuler un rendez-vous, et les motifs d'absence ne s'affichent pas.
        </p>
        <p className="text-slate-500">
          Il se connecte avec <strong>son compte PharmaGestion habituel</strong>, sur{' '}
          <strong>rh.pharmagestion.fr</strong>. L'accès vaut pour <strong>cette officine
          seulement</strong> : en multi-site, positionnez-vous d'abord sur la bonne.
        </p>
        <p className="text-slate-500">
          <strong>Ouvrez l'accès avant sa première visite sur PharmaRH.</strong> Sans accès, le
          site lui propose de créer sa propre pharmacie, et son adresse ne peut plus servir ici.
        </p>
      </div>

      <label className="label">Adresse e-mail de son compte PharmaGestion</label>
      <div className="flex gap-2">
        <input
          type="email"
          value={email}
          onChange={(e) => setEmail(e.target.value)}
          onKeyDown={(e) => { if (e.key === 'Enter') handleAdd(); }}
          placeholder="titulaire@exemple.fr"
          className="input flex-1"
        />
        <Button variant="primary" icon={Plus} loading={add.isPending} disabled={!email.trim()} onClick={handleAdd}>
          Ouvrir l'accès
        </Button>
      </div>
      {add.isError && !alreadyTitulaire && (
        <div className="mt-2 text-[12px] text-danger flex items-start gap-1.5">
          <AlertCircle size={13} className="mt-0.5 flex-shrink-0" />
          {(add.error as Error).message}
        </div>
      )}
      {alreadyTitulaire && (
        <div className="mt-3 bg-amber-50 border border-amber-200 rounded-xl p-4 text-[12.5px] text-slate-700 leading-relaxed space-y-2">
          <div className="flex items-start gap-1.5 font-semibold text-slate-900">
            <AlertCircle size={14} className="mt-0.5 flex-shrink-0 text-amber-600" />
            Cette adresse a déjà son propre espace RH sur PharmaRH
          </div>
          <p>
            En général, c'est qu'il est allé sur <strong>rh.pharmagestion.fr</strong> avant que vous
            lui ouvriez l'accès : le site lui a proposé de créer sa pharmacie, et il l'a fait. Une
            même adresse ne peut pas avoir son propre espace <strong>et</strong> un accès en
            consultation chez vous.
          </p>
          <p className="font-medium text-slate-900">Pour débloquer :</p>
          <ol className="list-decimal pl-5 space-y-1">
            <li>
              Il se connecte sur <strong>rh.pharmagestion.fr</strong>, puis va dans{' '}
              <strong>Paramètres → Zone de danger → « Réinitialiser la pharmacie »</strong>.
            </li>
            <li>
              Avant de valider, l'écran affiche ce qui sera effacé. Il doit indiquer{' '}
              <strong>0 salarié</strong>. S'il y en a, il se sert vraiment de son espace :{' '}
              <strong>il ne supprime rien</strong>, et l'accès en consultation n'est pas possible
              avec cette adresse.
            </li>
            <li>
              Une fois la pharmacie supprimée, le site lui propose d'en créer une :{' '}
              <strong>il ferme l'onglet sans rien remplir</strong>.
            </li>
            <li>
              Revenez ici et cliquez de nouveau sur <strong>« Ouvrir l'accès »</strong>. À sa
              prochaine connexion, il arrivera sur l'écran du comptoir de votre officine.
            </li>
          </ol>
        </div>
      )}

      <div className="mt-5">
        {isLoading ? (
          <div className="text-[12px] text-slate-500">Chargement…</div>
        ) : !viewers?.length ? (
          <div className="text-[12px] text-slate-500">Aucun accès en consultation pour l'instant.</div>
        ) : (
          <div className="space-y-1.5">
            {viewers.map((v) => (
              <div key={v.id} className="flex items-center gap-3 px-3 py-2 rounded-lg border border-slate-200">
                <Eye size={14} className="text-slate-400 flex-shrink-0" />
                <div className="flex-1 min-w-0">
                  <div className="text-[12.5px] text-slate-800 truncate">{v.email}</div>
                  <div className="text-[10.5px] text-slate-400">
                    Ouvert le {new Date(v.createdAt).toLocaleDateString('fr-FR')}
                  </div>
                </div>
                <button
                  onClick={() => handleRemove(v.id, v.email)}
                  disabled={remove.isPending}
                  className="text-[12px] text-slate-500 hover:text-danger px-2 py-1 rounded-md hover:bg-slate-50 flex items-center gap-1"
                  title="Retirer l'accès"
                >
                  <Trash2 size={12} />
                  Retirer
                </button>
              </div>
            ))}
          </div>
        )}
      </div>
    </Section>
  );
}

// ============================================================================
// Zone de danger — réinitialisation
// ============================================================================

/**
 * Efface la pharmacie et tout ce qu'elle contient, sans retour possible.
 *
 * Trois freins, du plus doux au plus dur :
 *  1. la section est à part, en bas, et signalée comme dangereuse ;
 *  2. les CHIFFRES RÉELS de ce qui va disparaître sont affichés avant tout —
 *    « 14 salariés, 2 380 journées de planning » arrête mieux la main que
 *    n'importe quelle formulation d'alerte ;
 *  3. il faut retaper le NOM EXACT de la pharmacie. Une case à cocher se coche
 *    par réflexe ; retaper un nom oblige à lire ce qu'on détruit.
 *
 * Le serveur revérifie le nom : ces freins protègent de l'erreur, pas d'un
 * appel direct à l'API.
 */
function DangerZoneSection() {
  const [armed, setArmed] = useState(false);
  const [typed, setTyped] = useState('');
  const preview = useResetPreview(armed);
  const resetMut = useResetPharmacy();

  const data = preview.data;
  const nameMatches =
    !!data && typed.trim().toLocaleLowerCase('fr') === data.pharmacyName.trim().toLocaleLowerCase('fr');

  async function handleReset() {
    try {
      await resetMut.mutateAsync({ confirmation: typed });
      // Le compte vient de disparaître avec la pharmacie : le jeton en poche ne
      // vaut plus rien. On repart de la racine — la session PharmaGestion, elle,
      // est toujours ouverte, donc le titulaire retombe sur le formulaire de
      // création. `replace` pour qu'un retour arrière ne ramène pas sur un
      // écran qui n'existe plus.
      auth.clearToken();
      window.location.replace('/');
    } catch {
      // Erreur affichée via resetMut.error
    }
  }

  return (
    <Section
      title="Réinitialiser la pharmacie"
      description="Efface définitivement toutes les données de l'officine. Vous pourrez ensuite la recréer avec le même compte."
    >
      <div className="border border-danger/40 rounded-xl overflow-hidden">
        <div className="bg-danger-bg px-4 py-3 flex items-start gap-2.5">
          <ShieldAlert size={16} className="text-danger mt-0.5 flex-shrink-0" />
          <div className="text-[12px] text-danger leading-relaxed">
            <strong>Cette action est irréversible.</strong> Salariés, plannings, absences,
            rendez-vous, fiches navette et compteurs d'heures seront supprimés. Il n'existe
            aucune sauvegarde permettant de revenir en arrière.
          </div>
        </div>

        <div className="p-4">
          {!armed ? (
            <button
              onClick={() => setArmed(true)}
              className="text-[13px] font-medium text-danger border border-danger/40 rounded-lg px-4 py-2 hover:bg-danger-bg transition-colors"
            >
              Réinitialiser la pharmacie…
            </button>
          ) : preview.isLoading ? (
            <div className="text-[13px] text-slate-500">Calcul de ce qui sera supprimé…</div>
          ) : preview.isError ? (
            <div className="text-[12px] text-danger">
              Impossible de lire l'état de la pharmacie. Réessayez.
            </div>
          ) : data?.multiSite ? (
            // Le compte titulaire n'est rattaché qu'à UNE pharmacie : l'effacer
            // laisserait les autres officines sans personne pour y entrer.
            <div className="text-[12.5px] text-slate-700 leading-relaxed">
              <strong>Réinitialisation indisponible :</strong> votre groupe compte{' '}
              {data.sitesInGroup} officines. Supprimez d'abord les autres depuis{' '}
              <strong>Gestion multi-site</strong> — réinitialiser celle-ci laisserait les
              autres sans accès.
            </div>
          ) : data ? (
            <>
              <div className="text-[12.5px] text-slate-700 mb-3">
                Seront définitivement supprimés :
              </div>
              <ul className="text-[12.5px] text-slate-700 space-y-1 mb-4">
                <li>· <strong>{data.counts.employees}</strong> salarié(s)</li>
                <li>· <strong>{data.counts.schedules}</strong> journée(s) de planning</li>
                <li>· <strong>{data.counts.absences}</strong> absence(s)</li>
                <li>· <strong>{data.counts.appointments}</strong> rendez-vous</li>
                <li>· <strong>{data.counts.shuttleReports}</strong> fiche(s) navette</li>
                <li>· vos réglages, vos horaires d'ouverture et votre catalogue</li>
              </ul>

              <label className="label">
                Pour confirmer, saisissez&nbsp;
                <span className="font-semibold text-slate-900">{data.pharmacyName}</span>
              </label>
              <input
                className="input"
                value={typed}
                onChange={(e) => setTyped(e.target.value)}
                placeholder={data.pharmacyName}
                autoComplete="off"
              />

              {resetMut.error && (
                <div className="mt-3 bg-danger-bg border border-danger/30 rounded-lg p-2.5 flex items-start gap-2 text-[11.5px] text-danger">
                  <AlertCircle size={13} className="mt-0.5 flex-shrink-0" />
                  <span>{(resetMut.error as Error).message || 'Réinitialisation impossible'}</span>
                </div>
              )}

              <div className="flex items-center gap-2 mt-4">
                <button
                  onClick={handleReset}
                  disabled={!nameMatches || resetMut.isPending}
                  className="text-[13px] font-medium text-white bg-danger rounded-lg px-4 py-2 disabled:opacity-40 disabled:cursor-not-allowed hover:opacity-90 transition-opacity"
                >
                  {resetMut.isPending ? 'Suppression…' : 'Supprimer définitivement'}
                </button>
                <button
                  onClick={() => {
                    setArmed(false);
                    setTyped('');
                    resetMut.reset();
                  }}
                  className="text-[13px] text-slate-500 hover:text-slate-900 px-2"
                >
                  Annuler
                </button>
              </div>
            </>
          ) : null}
        </div>
      </div>
    </Section>
  );
}
